import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("jr_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch (e) {
      localStorage.removeItem("jr_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const setAuth = (userObj, token) => {
    if (token) localStorage.setItem("jr_token", token);
    setUser(userObj);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithPassword = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setAuth(res.data.user, res.data.session_token);
    return res.data.user;
  };

  const registerWithPassword = async (name, email, password) => {
    const res = await api.post("/auth/register", { name, email, password });
    setAuth(res.data.user, res.data.session_token);
    return res.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.error("Logout request failed:", err); }
    localStorage.removeItem("jr_token");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, setAuth, loading, loginWithGoogle, loginWithPassword, registerWithPassword, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
